import React from "react";
import { Box } from "@mui/joy";
import { SxProps } from "@mui/joy/styles/types";
import blackHole from "../../assets/img/blackHole.png";

export const HERO_BG = "#0b0d12";

/**
 * Below this card width the key visual is hidden entirely so the text can use
 * the full card width (narrow dashboard columns / mobile). Consumers use
 * HERO_IMAGE_HIDDEN_QUERY to widen their text padding in the same breath.
 */
const IMAGE_MIN_CARD_WIDTH = 640;
export const HERO_IMAGE_HIDDEN_QUERY = `@container (max-width: ${IMAGE_MIN_CARD_WIDTH - 1}px)`;

/**
 * The visual stops growing beyond this width so its fade-out band (96% of the
 * layer ≈ 653px) always completes before the text column, whose padding is
 * capped at 664px — on very wide cards image and text would otherwise overlap.
 */
const IMAGE_MAX_WIDTH = 680;

interface Props {
  children: React.ReactNode;
  style?: React.CSSProperties;
  /** Which side the key visual sits on; it blurs out towards the text. */
  imageSide?: "left" | "right";
  /** Vertical fine-tuning of the visual in px — negative moves it up. */
  imageOffsetY?: number;
}

/**
 * Dark hero shell shared by the dashboard welcome banner and the About-page
 * welcome section. The corporate key visual is rendered SHARP on its side of
 * the card; only a narrow band at the text edge transitions out, where a
 * blurred copy of the image takes over as the sharp one dissolves — so the
 * image visibly blurs into the dark background instead of merely dimming.
 * Both layers share the same background geometry and stay pixel-aligned.
 */
const WelcomeHeroSurface: React.FC<Props> = ({ children, style, imageSide = "left", imageOffsetY = 0 }) => {
  const dir = imageSide === "left" ? "90deg" : "270deg";
  const sharpMask = `linear-gradient(${dir}, rgba(0,0,0,1) 70%, rgba(0,0,0,0) 96%)`;
  const blurMask = `linear-gradient(${dir}, rgba(0,0,0,0) 62%, rgba(0,0,0,1) 74%, rgba(0,0,0,1) 84%, rgba(0,0,0,0) 100%)`;

  const imageLayer = (mask: string, filter?: string): SxProps => ({
    position: "absolute",
    top: 0,
    bottom: 0,
    [imageSide]: 0,
    width: `min(45%, ${IMAGE_MAX_WIDTH}px)`,
    backgroundImage: `url(${blackHole})`,
    backgroundSize: "cover",
    backgroundPosition: `center calc(50% + ${imageOffsetY}px)`,
    WebkitMaskImage: mask,
    maskImage: mask,
    filter,
    [HERO_IMAGE_HIDDEN_QUERY]: { display: "none" },
  });

  return (
    <div
      style={{
        position: "relative",
        width: "100%",
        height: "100%",
        containerType: "inline-size",
        backgroundColor: HERO_BG,
        borderRadius: 8,
        overflow: "hidden",
        ...style,
      }}
    >
      <Box aria-hidden sx={imageLayer(blurMask, "blur(14px)")} />
      <Box aria-hidden sx={imageLayer(sharpMask)} />
      <div style={{ position: "relative", zIndex: 1, height: "100%" }}>{children}</div>
    </div>
  );
};

export default WelcomeHeroSurface;
